/**
 * @puffin-core/auth-client — canonical auth endpoint maps.
 *
 * All paths are **base-relative** to the app's auth base (the transport adapter
 * prepends the base, e.g. `/api/v1/auth`). Apps override only the divergent entries via `createAuthService`'s
 * `endpoints` option (today: pos-web's device routes). N152 retired port-web's MFA setup map, which named routes
 * port-api never served: the MFA setup paths are the shared `createMfaSetupRouter`'s, in every app.
 *
 * Verified against the live per-app source + the pos-api `@puffin-core/auth-routes`
 * route registrations (AUTHSVC-CONSOLIDATION-001 P0 decision matrix).
 *
 * @module @puffin-core/auth-client/endpoints
 */

/**
 * Canonical tenant auth endpoint map (pos-web / planner / prep / palm / etc.).
 * Frozen so accidental in-place mutation by a caller is impossible.
 */
export const AUTH_ENDPOINTS = Object.freeze({
  // Core session
  tenantLogin: '/tenant/login',
  verifyCredentials: '/verify-credentials',
  verifyMfa: '/mfa/verify',
  // Q-EE-34 P5: the passkey second factor's challenge, for the person a live sign-in challenge names.
  verifyMfaPasskeyOptions: '/mfa/verify/passkey/options',
  sendMfaOtp: '/mfa/send-otp',
  tenantLogout: '/tenant/logout',
  tenantValidate: '/tenant/validate',
  crossAppExchange: '/cross-app/exchange',
  companyValidate: '/company/validate',
  tenantRefresh: '/tenant/refresh',
  requestPasswordReset: '/tenant/request-password-reset',
  // MFA setup — the shared `createMfaSetupRouter` every API mounts (N152: no app diverges from these paths)
  initMfaSetup: '/mfa/setup/init',
  verifyMfaSetup: '/mfa/setup/verify',
  getMfaStatus: '/mfa/status',
  regenerateBackupCodes: '/mfa/backup-codes/regenerate',
  disableMfa: '/mfa/disable',
  // The ENROLMENT pair a sign-in's MFA_SETUP_REQUIRED refusal hands over (owner ruling Q-ARG-1): the shared setup
  // routes every API mounts, named apart from the in-session setup keys above so a per-app override of those can never
  // re-point the enrolment.
  mfaEnrollmentBegin: '/mfa/setup/init',
  mfaEnrollmentComplete: '/mfa/setup/verify',
  // Security (pos)
  getSecurityOverview: '/security/overview',
  signOutAllSessions: '/sessions/signout-all',
  // Devices (pos)
  getDevices: '/devices',
  revokeDevice: '/devices', // + /:deviceId appended by the operation
  registerDevicePin: '/device-pin/register',
  loginWithPin: '/device-pin/login',
  // Passkey HTTP legs — exactly the routes `@puffin-core/passkey` `createPasskeyRouter` serves, and
  // every API mounts that router at `/passkey` under its auth base, so no app overrides them. A
  // palm-only `/passkey/authenticate/options` override outlived the route it named by six months
  // (USER-FACING-GEMS L1); `passkeyClientRoutesResolve.contract` now proves each leg resolves.
  passkeyRegisterOptions: '/passkey/register/options',
  passkeyRegister: '/passkey/register',
  passkeyDiscoverableOptions: '/passkey/discoverable/options',
  passkeyAuthenticate: '/passkey/authenticate',
  passkeyList: '/passkey/list',
  passkeyDelete: '/passkey', // + /:passkeyId appended by the operation
});

/**
 * Canonical platform-admin auth endpoint map (the transport adapter prepends the
 * platform base, {@link PLATFORM_AUTH_BASE_URL}).
 */
export const PLATFORM_AUTH_ENDPOINTS = Object.freeze({
  login: '/login',
  verifyMfa: '/mfa/verify',
  // Q-EE-34 P5: the passkey second factor's challenge, for the staff member a live sign-in challenge names.
  verifyMfaPasskeyOptions: '/mfa/verify/passkey/options',
  logout: '/logout',
  validate: '/validate',
  refresh: '/refresh',
  // Q-L16-98 · F-98-16: first-factor enrolment through the login gate's enrolment token.
  mfaEnrollmentBegin: '/mfa/setup/init',
  mfaEnrollmentComplete: '/mfa/setup/verify',
});

/**
 * The platform auth mount, as EVERY platform console reaches it: same-origin, under `/api`.
 *
 * 🔴 Not a style choice (UNREACHABLE-MODULE-BURNDOWN-001 unit 3f, owner ruling Q-UMB-19). A
 * platform session's refresh credential is the httpOnly `platform_refresh` cookie, which the
 * server issues at `Path=/api/v1/auth/platform` (`@puffin-core/auth-routes` refreshSession.js).
 * A browser sends it only to a URL under that path on the origin that set it — so a console that
 * reaches its platform auth anywhere else (port-web's old absolute `api.portpuffin.com/v1/port/…`
 * base) can neither refresh nor revoke at logout.
 */
export const PLATFORM_AUTH_BASE_URL = '/api/v1/auth/platform';

/** Where a platform console refreshes: the cookie leg of `POST {PLATFORM_AUTH_BASE_URL}/refresh`. */
export const PLATFORM_REFRESH_ENDPOINT = `${PLATFORM_AUTH_BASE_URL}${PLATFORM_AUTH_ENDPOINTS.refresh}`;
